import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, Lock, User, TrendingUp } from "lucide-react";

export const AuthModal = ({ open, onOpenChange }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        toast.success("Welcome back to the terminal");
      } else {
        await register(name, email, password);
        toast.success("Account created — welcome to Netroy");
      }
      onOpenChange(false);
      setEmail(""); setPassword(""); setName("");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="auth-modal-dialog"
        className="bg-[#0C1017] border border-white/10 text-white max-w-md p-0 overflow-hidden"
      >
        <div className="bg-gradient-to-br from-[#101620] to-[#0C1017] px-7 pt-7 pb-5 border-b border-white/5">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-9 w-9 rounded-md bg-brand/15 border border-brand/40 grid place-items-center">
              <TrendingUp className="h-5 w-5 text-brand" />
            </div>
            <span className="font-heading font-extrabold tracking-tight text-lg">NETROY</span>
          </div>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-extrabold tracking-tight">
              {mode === "login" ? "Access Terminal" : "Create Trader Account"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 mt-1">
            {mode === "login"
              ? "Sign in to unlock the AI Chart Analyzer & Community floor."
              : "Join thousands of serious traders. Free to start."}
          </p>
        </div>

        <form onSubmit={submit} className="px-7 py-6 space-y-4">
          {mode === "register" && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-slate-400 font-data">Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <Input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Alex Morgan"
                  className="pl-9 bg-[#070A0E] border-white/10 focus-visible:ring-brand h-11"
                  required
                />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400 font-data">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@trading.com"
                className="pl-9 bg-[#070A0E] border-white/10 focus-visible:ring-brand h-11"
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-slate-400 font-data">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                data-testid="auth-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="pl-9 bg-[#070A0E] border-white/10 focus-visible:ring-brand h-11"
                required
              />
            </div>
          </div>

          {error && (
            <p data-testid="auth-error-message" className="text-sm text-loss bg-loss/10 border border-loss/20 rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <Button
            data-testid="auth-submit-action-button"
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-brand hover:bg-brand-hover text-white font-semibold"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Sign In" : "Create Account"}
          </Button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-xs"><span className="bg-[#0C1017] px-3 text-slate-500 uppercase tracking-widest font-data">or</span></div>
          </div>

          <Button
            data-testid="auth-google-sso-button"
            type="button"
            onClick={googleLogin}
            variant="outline"
            className="w-full h-11 bg-transparent border-white/15 hover:bg-white/5 text-white gap-2"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Continue with Google
          </Button>

          <p className="text-center text-sm text-slate-400">
            {mode === "login" ? "New to Netroy?" : "Already have an account?"}{" "}
            <button
              data-testid="auth-toggle-login-register"
              type="button"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-brand hover:text-brand-hover font-semibold"
            >
              {mode === "login" ? "Create account" : "Sign in"}
            </button>
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
